import type { TicketPayment } from "@/services/api/ticketPayments";
import { getDisplayName, getInitials } from "@/lib/user";
import { formatINR } from "@/lib/currency";
import { formatDate } from "@/lib/dateFormat";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import type { StatusTone } from "@/components/StatusBadge/StatusBadge";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import styles from "../Ticketing.module.scss";

interface TicketCardProps {
  order: TicketPayment;
  actions: RowAction[];
  statusTone: StatusTone;
  methodLabel: string;
}

const TicketCard = ({ order, actions, statusTone, methodLabel }: TicketCardProps) => {
  const memberName = getDisplayName(order, { fallbackLabel: "Member" });
  const initials = getInitials(memberName) || "M";
  const orderId = order.paymentId || `Order #${order.id}`;
  const placedDate = formatDate(order.createdAt, { day: "numeric", month: "short", year: "numeric" });
  const formattedTicket = formatINR(order.ticketAmount);
  const formattedTax = formatINR(order.taxAmount);
  const formattedTotal = formatINR(order.totalAmount);

  return (
    <div className={styles.mobileCard}>
      {/* Top Header: Avatar, Member Name, Order ID, Status & Actions */}
      <div className={styles.mobileCardHeader}>
        <div className={styles.mobileAvatar}>
          <span>{initials}</span>
        </div>

        <div className={styles.mobileMemberInfo}>
          <h3 className={styles.mobileMemberName}>{memberName}</h3>
          <span className={styles.mobileOrderId} title={orderId}>
            {orderId}
          </span>
        </div>

        <div className={styles.mobileCardActions}>
          <StatusBadge
            label={order.paymentStatus || "—"}
            tone={statusTone}
            className={styles.mobileStatusBadge}
          />
          <RowActionsMenu
            actions={actions}
            ariaLabel={`Actions for ${orderId}`}
            triggerClassName={styles.mobileRowActionTrigger}
          />
        </div>
      </div>

      {/* Gathering & Date Details */}
      <div className={styles.mobileMetaRow}>
        <div className={styles.mobileGatheringCol}>
          <span className={styles.mobileMetaLabel}>Gathering</span>
          <span className={styles.mobileGatheringValue} title={order.eventName}>
            {order.eventName || "—"}
          </span>
        </div>
        <div className={styles.mobilePlacedCol}>
          <span className={styles.mobileMetaLabel}>Placed</span>
          <span className={styles.mobilePlacedValue}>{placedDate}</span>
        </div>
      </div>

      {/* Financial Breakdown (Ticket, GST, Total) */}
      <div className={styles.mobileFinancialBox}>
        <div className={styles.mobileFinancialItem}>
          <span className={styles.mobileFinancialLabel}>Ticket</span>
          <span className={styles.mobileFinancialValue}>{formattedTicket}</span>
        </div>
        <div className={styles.mobileFinancialDivider} />
        <div className={styles.mobileFinancialItem}>
          <span className={styles.mobileFinancialLabel}>GST</span>
          <span className={styles.mobileFinancialValue}>{formattedTax}</span>
        </div>
        <div className={styles.mobileFinancialDivider} />
        <div className={styles.mobileFinancialItem}>
          <span className={styles.mobileFinancialLabel}>Total</span>
          <span className={styles.mobileFinancialTotalValue}>{formattedTotal}</span>
        </div>
      </div>

      {/* Footer: Payment Method */}
      <div className={styles.mobileCardFooter}>
        <span className={styles.mobileFooterLabel}>Payment method</span>
        <span className={styles.mobileFooterValue}>{methodLabel}</span>
      </div>
    </div>
  );
};

export default TicketCard;
